import {Component, OnInit} from '@angular/core';
import {CommonModule, formatDate} from '@angular/common';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {forkJoin} from 'rxjs';
import {DatePickerModule} from 'primeng/datepicker';

import {RegisterService} from '../../../services/register.service';
import {FieldService} from '../../../services/field.service';
import {ClusterService} from '../../../services/cluster.service';
import {EntryService} from '../../../services/entry.service';
import {AuthService} from '../../../services/auth.service';

@Component({
    selector: 'app-entry-form',
    imports: [CommonModule, ReactiveFormsModule, DatePickerModule],
    templateUrl: './entry-form.component.html',
    styleUrl: './entry-form.component.css'
})
export class EntryFormComponent implements OnInit {
  entryForm: FormGroup;
  fields: any[] = [];
  register: any;
  steps: any[] = [];
  currentStep: number = 1;
  maxStep: number = 1;
  registerId: number | null = null;
  entryId: number | null = null;
  isEditMode: boolean = false;
  entryStatus: string | null = null;
  isAdminUser: boolean = false;
  isLocked: boolean = false;

  clusterValuesMap: Record<number, any[]> = {};

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private registerService: RegisterService,
    private fieldService: FieldService,
    private clusterService: ClusterService,
    private entryService: EntryService,
    private auth: AuthService,
    private toastr: ToastrService
  ) {
    this.entryForm = this.fb.group({
      values: this.fb.array([])
    });
  }

  ngOnInit(): void {
    const roles = this.getUserRoles();
      const isPlatformSuperuser = this.auth.isSuperUser();
      this.isAdminUser = roles.includes('Admin') && !isPlatformSuperuser;
      if (isPlatformSuperuser) {
      this.router.navigate(['/dashboard']);
      return;
    }

    const registerIdParam = this.route.snapshot.paramMap.get('registerId');
    const entryIdParam = this.route.snapshot.paramMap.get('id');

    if (entryIdParam && !registerIdParam) {
      this.entryId = Number(entryIdParam);
      this.isEditMode = true;
      this.loadEntry(this.entryId);
    } else if (registerIdParam) {
      this.registerId = Number(registerIdParam);
      this.loadRegisterAndFields(this.registerId);
    }
  }

  private getUserRoles(): string[] {
    const userInfo = this.auth.getCurrentUserInfo();
    const role = userInfo?.role;
    if (Array.isArray(role)) {
      return role;
    }
    return role ? [role] : [];
  }

  get valuesArray(): FormArray {
    return this.entryForm.get('values') as FormArray;
  }

  loadRegisterAndFields(registerId: number): void {
    forkJoin({
      register: this.registerService.getRegister(registerId),
      fields: this.fieldService.listFieldsByRegister(registerId)
    }).subscribe({
      next: ({ register, fields }) => {
        this.register = register;
        this.fields = fields || [];
        this.prepareSteps();
        this.buildForm();
        this.loadClusterValues();
      }
    });
  }

  loadEntry(entryId: number): void {
    this.entryService.getEntry(entryId).subscribe({
      next: (entry: any) => {
        this.entryStatus = entry.status;
        this.registerId = entry.register;
        this.currentStep = entry.current_step || 1;
        this.entryId = entry.id;
        forkJoin({
          register: this.registerService.getRegister(entry.register),
          fields: this.fieldService.listFieldsByRegister(entry.register)
        }).subscribe({
          next: ({ register, fields }) => {
            this.register = register;
            this.fields = fields || [];
            this.prepareSteps();
            this.buildForm();
            this.loadClusterValues();
            this.patchEntryValues(entry.entry_values || []);
            if (this.currentStep > this.maxStep) {
              this.currentStep = this.maxStep;
            }
            this.applyLocking();
          }
        });
      }
    });
  }

  prepareSteps(): void {
    if (Array.isArray(this.register?.steps_config) && this.register.steps_config.length) {
      this.steps = this.register.steps_config;
    } else {
      const stepNumbers = Array.from(new Set(this.fields.map(field => field.step_number || 1))).sort((a, b) => a - b);
      this.steps = stepNumbers.length ? stepNumbers.map(step => ({ step, label: `Step ${step}` })) : [{ step: 1, label: 'Step 1' }];
    }
    this.maxStep = Math.max(...this.steps.map(step => step.step));
  }

  get stepLabel(): string {
    const current = this.steps.find(step => step.step === this.currentStep);
    return current?.label || `Step ${this.currentStep}`;
  }

  buildForm(): void {
    this.valuesArray.clear();
    this.fields.forEach(field => {
      const validators = this.buildValidators(field);
      const defaultValue = field.data_type === 'checkbox' ? [] : null;
      this.valuesArray.push(this.fb.group({
        field_key: [field.field_key],
        data_type: [field.data_type],
        step_number: [field.step_number],
        name: [field.name],
        cluster: [field.cluster],
        value: [defaultValue, validators],
        file_url: [null, field.is_mandatory && (field.data_type === 'file' || field.data_type === 'image') ? Validators.required : []],
        file_type: [null],
        file_size: [null],
        is_mandatory: [field.is_mandatory]
      }));
    });
  }

  buildValidators(field: any): any[] {
    const validators = [];
    if (field.is_mandatory && field.data_type !== 'file' && field.data_type !== 'image') {
      validators.push(Validators.required);
    }
    if (field.min_length) {
      validators.push(Validators.minLength(field.min_length));
    }
    if (field.max_length) {
      validators.push(Validators.maxLength(field.max_length));
    }
    if (field.is_numeric) {
      validators.push(Validators.pattern(/^\d+$/));
    }
    if (field.is_decimal) {
      validators.push(Validators.pattern(/^\d+(\.\d+)?$/));
    }
    if (field.data_type === 'checkbox' && field.is_mandatory) {
      validators.push(this.arrayRequiredValidator);
    }
    return validators;
  }

  arrayRequiredValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (Array.isArray(value) && value.length > 0) {
      return null;
    }
    return { required: true };
  }

  loadClusterValues(): void {
    const clusterIds = Array.from(new Set(this.fields.map(field => field.cluster).filter(Boolean)));
    if (!clusterIds.length) {
      return;
    }
    const requests = clusterIds.map((clusterId: number) =>
      this.clusterService.listClusterValues(clusterId, 1, 200)
    );
    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        responses.forEach((res, index) => {
          const clusterId = clusterIds[index];
          this.clusterValuesMap[clusterId] = res?.results ?? res ?? [];
        });
      }
    });
  }

  patchEntryValues(values: any[]): void {
    values.forEach((entryValue: any) => {
      const index = this.fields.findIndex(field => field.field_key === entryValue.field_key);
      if (index === -1) {
        return;
      }
      const group = this.valuesArray.at(index) as FormGroup;
      if (entryValue.file_url) {
        group.patchValue({
          file_url: entryValue.file_url,
          file_type: entryValue.file_type,
          file_size: entryValue.file_size
        });
      } else {
        const dataType = group.get('data_type')?.value;
        if (dataType === 'checkbox') {
          const valueArray = Array.isArray(entryValue.value) ? entryValue.value : [entryValue.value];
          group.patchValue({ value: valueArray.filter((item: any) => item !== null && item !== undefined) });
        } else {
          let normalizedValue = entryValue.value;
          if (dataType === 'date' && typeof normalizedValue === 'string') {
            normalizedValue = new Date(normalizedValue);
          }
          if (dataType === 'datetime' && typeof normalizedValue === 'string') {
            normalizedValue = new Date(normalizedValue);
          }
          group.patchValue({ value: normalizedValue });
        }
      }
    });
  }

  applyLocking(): void {
    if (this.entryStatus === 'approved' && !this.isAdminUser) {
      this.isLocked = true;
    }
    if (this.entryStatus === 'under_review' && !this.isAdminUser) {
      this.isLocked = true;
    }
    if (this.isLocked) {
      this.entryForm.disable();
    }
  }

  getCurrentStepFields(): any[] {
    return this.fields.map((field, index) => ({ field, index }))
      .filter(item => item.field.step_number === this.currentStep);
  }

  markStepTouched(): void {
    this.getCurrentStepFields().forEach(item => {
      const group = this.valuesArray.at(item.index) as FormGroup;
      group.markAllAsTouched();
    });
  }

  isStepValid(): boolean {
    return this.getCurrentStepFields().every(item => (this.valuesArray.at(item.index) as FormGroup).valid);
  }

  buildPayloadForStep(): any[] {
    return this.getCurrentStepFields().map(item => {
      const group = this.valuesArray.at(item.index) as FormGroup;
      const dataType = group.get('data_type')?.value;
      const payload: any = { field_key: group.get('field_key')?.value };
      if (dataType === 'file' || dataType === 'image') {
        payload.file_url = group.get('file_url')?.value;
        payload.file_type = group.get('file_type')?.value;
        payload.file_size = group.get('file_size')?.value;
      } else {
        const value = group.get('value')?.value;
        if (dataType === 'date' && value instanceof Date) {
          payload.value = formatDate(value, 'yyyy-MM-dd', 'en-US');
        } else if (dataType === 'datetime' && value instanceof Date) {
          payload.value = value.toISOString();
        } else {
          payload.value = value;
        }
      }
      return payload;
    });
  }

  submitStep(status?: string): void {
    if (this.isLocked) {
      return;
    }
    if (!this.isStepValid()) {
      this.markStepTouched();
      this.toastr.error('Please fix validation errors.', 'Error');
      return;
    }

    const valuesPayload = this.buildPayloadForStep();
    const payload: any = {
      register: this.registerId,
      location: this.register?.location?.id || this.register?.location || null,
      current_step: this.currentStep,
      values: valuesPayload
    };
    if (status) {
      payload.status = status;
    }

    if (!this.entryId) {
      if (this.currentStep !== 1) {
        this.toastr.error('Start with step 1 to create an entry.', 'Error');
        return;
      }
      this.entryService.createEntry(payload).subscribe({
        next: (res: any) => {
          this.entryId = res.id;
          if (this.currentStep < this.maxStep && !status) {
            this.currentStep += 1;
          } else {
            this.toastr.success('Entry submitted successfully.', 'Success');
            this.router.navigate(['/visitor-management/select-register']);
          }
        }
      });
    } else {
      this.entryService.updateEntry(this.entryId, payload).subscribe({
        next: () => {
          if (this.currentStep < this.maxStep && !status) {
            this.currentStep += 1;
          } else if (status) {
            this.toastr.success('Entry submitted successfully.', 'Success');
            this.router.navigate(['/visitor-management/entries']);
          }
        }
      });
    }
  }

  goNext(): void {
    if (this.currentStep < this.maxStep) {
      this.submitStep();
    }
  }

  goPrevious(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  submitFinal(): void {
    if (this.currentStep < this.maxStep) {
      this.toastr.error('Complete all steps before submitting.', 'Error');
      return;
    }
    const status = 'submitted';
    this.submitStep(status);
  }

  onFileSelected(event: any, index: number): void {
    const file: File = event.target.files[0];
    if (!file) {
      return;
    }
    const group = this.valuesArray.at(index) as FormGroup;
    this.entryService.uploadFile(file).subscribe({
      next: (res: any) => {
        group.patchValue({
          file_url: res.file_url || res.url || '',
          file_type: res.file_type || file.type,
          file_size: res.file_size || file.size
        });
        group.get('file_url')?.markAsTouched();
      },
      error: () => {
        this.toastr.error('File upload failed.', 'Error');
      }
    });
  }

  getClusterValues(clusterId: number): any[] {
    return this.clusterValuesMap[clusterId] || [];
  }

  toggleCheckboxValue(index: number, option: string): void {
    const group = this.valuesArray.at(index) as FormGroup;
    const current = group.get('value')?.value || [];
    if (current.includes(option)) {
      group.get('value')?.setValue(current.filter((item: string) => item !== option));
    } else {
      group.get('value')?.setValue([...current, option]);
    }
    group.get('value')?.markAsTouched();
  }

  isOptionChecked(index: number, option: string): boolean {
    const group = this.valuesArray.at(index) as FormGroup;
    const current = group.get('value')?.value || [];
    return current.includes(option);
  }

  cancel(): void {
    if (this.isEditMode) {
      this.router.navigate(['/visitor-management/entries']);
    } else {
      this.router.navigate(['/visitor-management/select-register']);
    }
  }
}
